import { useState, useEffect } from 'react';
import { DEFAULT_RANGE } from '../global-constants';

/**
 * Hook to manage the radius state for location filtering.
 * Listens for radius changes published through DDC pubsub
 * (via ws-locations-data bus).
 *
 * @param {boolean} isReady - Whether the LocationsData API is ready to use
 * @returns {Object} Object containing radius state and update function
 * @returns {number} returns.radius - Current radius value for location filtering
 * @returns {function} returns.updateRadius - Function to reset radius filter to default and trigger search
 */
export const useLocationsRadius = (isReady) => {
	const [radius, setRadius] = useState(DEFAULT_RANGE);

	useEffect(() => {
		if (!isReady) {
			return () => {};
		}
		const LOCATION_DATA = window.DDC.LocationsData;
		if (!LOCATION_DATA.registerRadiusChangedNotification) {
			return () => {};
		}

		if (LOCATION_DATA.getRange) {
			// Load initial radius value
			const initialRadius = LOCATION_DATA.getRange();
			setRadius(initialRadius);
		}

		const unsubscribe = LOCATION_DATA.registerRadiusChangedNotification(
			(data) => {
				const newRadius = data?.radius;
				if (typeof newRadius === 'number') {
					setRadius(newRadius);
				}
			}
		);

		return unsubscribe;
	}, [isReady]);

	const updateRadius = () => {
		const locationsData = window.DDC.LocationsData;
		const postalCode = locationsData.getPostalCode();

		if (!postalCode || postalCode.trim() === '') {
			return;
		}

		const excludeByRange = true;
		locationsData.setSearchParams(
			postalCode,
			DEFAULT_RANGE,
			excludeByRange
		);
	};

	return {
		radius,
		updateRadius
	};
};
